import React from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import { Box, Divider, Grid, useTheme } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import * as yup from 'yup';
import Typographys from 'components/Atoms/Typography';
import DatePicker from 'components/Atoms/Datepicker';
import Button from 'components/Atoms/Button';
import CodeEditor from 'components/Molecules/CodeEditor';
import dayjs from 'dayjs';
import { empRecordActionDetailStyle } from '../style';

const StartEndDateForm = ({ heading, label, btnLabel, handleSubmit, handleCancel, loading, effectiveStartDate }) => {
  const theme = useTheme();

  const StartEndDateSchema = yup.object().shape({
    startDate: yup.mixed().required('Start date is required'),
    endDate: yup.mixed().required('End date is required'),
  });

  const formik = useFormik({
    initialValues: { startDate: effectiveStartDate ? dayjs(effectiveStartDate) : '', endDate: '', notes: '' },
    onSubmit: async (values, helpers) => {
      try {
        handleSubmit(values);
        // helpers.resetForm();
        helpers.setStatus({ success: true });
        helpers.setSubmitting(false);
      } catch (err) {
        helpers.setStatus({ success: false });
        helpers.setErrors({ submit: err.message });
        helpers.setSubmitting(false);
      }
    },
    validationSchema: StartEndDateSchema,
  });

  return (
    <>
      <Typographys variant="body2">{heading}</Typographys>

      <Box component="form" onSubmit={formik.handleSubmit}>
        <Box sx={empRecordActionDetailStyle.formWrapper}>
          <Grid container rowGap={2} columnSpacing={3}>
            <Grid item xs={12} sm={6} md={4}>
              <DatePicker
                error={Boolean(formik.touched.startDate && formik.errors.startDate)}
                onChange={(value) => formik.setFieldValue('startDate', Date.parse(value))}
                onBlur={formik.handleBlur}
                value={formik?.values?.startDate ? dayjs(formik?.values?.startDate) : ''}
                name="startDate"
                id="startDate"
                label={`${label} Start Date*`}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <DatePicker
                error={Boolean(formik.touched.endDate && formik.errors.endDate)}
                onChange={(value) => formik.setFieldValue('endDate', Date.parse(value))}
                onBlur={formik.handleBlur}
                value={formik?.values?.endDate ? dayjs(formik?.values?.endDate) : ''}
                name="endDate"
                id="endDate"
                label={`${label} End Date*`}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <CodeEditor
                error={Boolean(formik.touched.notes && formik.errors.notes)}
                helperText={formik.touched.notes && formik.errors.notes}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.notes}
                name="notes"
                id="notes"
                label={`${label} notes`}
              />
            </Grid>
          </Grid>
        </Box>

        <Box sx={empRecordActionDetailStyle.btnContainer}>
          <Divider sx={{ my: 2, borderColor: theme.palette.other[200] }} />
          <Box sx={empRecordActionDetailStyle.submitWrapper}>
            <Button
              size="large"
              color="info"
              variant="outlined"
              type="reset"
              onClick={() => {
                formik.resetForm();
                handleCancel();
              }}
            >
              Cancel
            </Button>
            <LoadingButton loading={loading} color="primary" size="large" type="submit" variant="contained">
              {btnLabel}
            </LoadingButton>
          </Box>
        </Box>
      </Box>
    </>
  );
};

StartEndDateForm.defaultProps = {
  heading: '',
  label: '',
  btnLabel: '',
  handleSubmit: () => {},
  handleCancel: () => {},
  loading: false,
  effectiveStartDate: '',
};

StartEndDateForm.propTypes = {
  heading: PropTypes.string,
  label: PropTypes.string,
  btnLabel: PropTypes.string,
  handleSubmit: PropTypes.func,
  handleCancel: PropTypes.func,
  loading: PropTypes.bool,
  effectiveStartDate: PropTypes.string,
};

export default StartEndDateForm;
